import { ReactElement, useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Formik, FormikHelpers } from 'formik'
import { Amplify } from 'aws-amplify'
import { resetPassword } from '@aws-amplify/auth'
import { Button, PageTitle } from '../../components/atoms'
import { Modal } from '../../components/molecules'
import AuthPageLayout from '../../components/global/AuthPageLayout'
import { toast } from 'react-toastify'
import ControlHighlight from '../../components/molecules/KeyboardControls/ControlHighlight'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import KeyboardControl from '../../components/molecules/KeyboardControls/KeyboardControl'
import { HighlightEffect } from '../../enums/KeyboardControls'
import ContactLinks from '../../components/molecules/ContactLinks/ContactLinks'
import { logger } from '../../utils'

export const pageTitle = 'Password Reset - Check your email'
export const idPasswordResetPageMainContent = 'password-reset-page-main-content'

export enum FocusableElement {
  ButtonResend = 'button-resend',
  LinkContact = 'link-contact',
}

function PasswordReset (): ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const keyboardControls = useKeyboardControls()

  const emailRef = useRef<string | undefined>(
    location.state?.email || localStorage.getItem('resetPasswordEmail') || undefined
  )

  // Only redirect if no email AND it's not a hash navigation (skip link click)
  useEffect(() => {
    if (emailRef.current) {
      localStorage.setItem('resetPasswordEmail', emailRef.current)
    } else if (!window.location.hash) {
      // If no email in ref or localStorage, and no hash, redirect
      navigate('/')
    }
  }, [navigate])

  const handleResendEmail = async (): Promise<void> => {
    const email = emailRef.current
    if (!email) {
      navigate('/')
      return
    }
    try {
      setIsSubmitting(true)
      const currentConfig = Amplify.getConfig()
      logger.log('Full Amplify configuration:')
      logger.log(JSON.stringify(currentConfig))
      const result = await resetPassword({ username: email })
      logger.log('Password reset request result:')
      logger.log(JSON.stringify(result))
      toast.success('Password reset email sent successfully', { position: toast.POSITION.TOP_CENTER })
    } catch (err: any) {
      logger.error('Error resending password reset email:')
      logger.error(err)
      toast.error('Failed to resend password reset email. Please try again.', { position: toast.POSITION.TOP_CENTER })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthPageLayout mainId={idPasswordResetPageMainContent}>
        <section className='w-full md:w-auto'>
          <Formik
            initialValues={{}}
            onSubmit={async (_values, actions: FormikHelpers<{}>): Promise<void> => {
              await handleResendEmail()
              actions.setSubmitting(false)
            }}
          >
            {() => (
              <Modal>
                <div className='flex flex-col h-full w-full'>
                  <PageTitle title={pageTitle} addScreenReaderH1={false} />
                  <h1 className='font-bold capitalize text-[20px] md:text-[26px] text-center'>
                    password reset
                  </h1>
                  <div className='flex-1 flex flex-col justify-center items-center overflow-y-auto'>
                    <p className='pt-8 pb-10 md:pt-14 md:pb-16 font-bold text-[28px] md:text-[38px] leading-none min-h-1024:pt-20 min-h-1024:pb-32'>
                      Check your email
                    </p>
                    <p className='text-[18px] md:text-[26px] w-full max-w-[430px] leading-normal md:leading-[3rem] font-thin text-center'>
                      Click the button in the email sent to{' '}
                      <span className='text-dopage-darkGreen'>{emailRef.current}</span>
                      <br />
                      <span className='sr-only'> </span>
                      to reset your password.
                      <br />
                      <br />
                      <br />
                    </p>
                  </div>
                  <div className='max-w-[440px] w-full flex-shrink-0 pb-4 mx-auto'>
                    <ControlHighlight
                      active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonResend)}
                      highlightEffect={HighlightEffect.ButtonHexOutline}
                      data-testid={`highlight-${FocusableElement.ButtonResend}`}
                    >
                      <KeyboardControl>
                        <Button
                          className='font-primary bg-dopage-primary w-full h-[86px] diamond-btn font-bold mt-[40px] flex justify-center items-center'
                          data-testid={FocusableElement.ButtonResend}
                          disabled={isSubmitting}
                          id={FocusableElement.ButtonResend}
                          loading={isSubmitting}
                          onClick={handleResendEmail}
                        >
                          <span className='text-[20px] md:text-[26px] text-neutral-090'>
                            Resend Email
                          </span>
                        </Button>
                      </KeyboardControl>
                    </ControlHighlight>
                    <ContactLinks
                      id={FocusableElement.LinkContact}
                    />
                  </div>
                </div>
              </Modal>
            )}
          </Formik>
        </section>
    </AuthPageLayout>
  )
}

export default PasswordReset
